import Bus from './bus'
import { getToken } from '@/utils/auth'
import defaultSettings from '@/settings'

const { sm4 } = require('sm-crypto')

/**
 * Parse the time to string
 * @param {(Object|string|number)} time
 * @param {string} cFormat
 * @returns {string | null}
 */
export function parseTime(time, cFormat) {
  if (arguments.length === 0 || !time) {
    return null
  }
  const format = cFormat || '{y}-{m}-{d} {h}:{i}:{s}'
  let date
  if (typeof time === 'object') {
    date = time
  } else {
    if (typeof time === 'string') {
      if (/^[0-9]+$/.test(time)) {
        // support "1548221490638"
        time = parseInt(time)
      } else {
        // support safari
        // https://stackoverflow.com/questions/4310953/invalid-date-in-safari
        time = time.replace(new RegExp(/-/gm), '/')
      }
    }

    if (typeof time === 'number' && time.toString().length === 10) {
      time = time * 1000
    }
    date = new Date(time)
  }
  const formatObj = {
    y: date.getFullYear(),
    m: date.getMonth() + 1,
    d: date.getDate(),
    h: date.getHours(),
    i: date.getMinutes(),
    s: date.getSeconds(),
    a: date.getDay()
  }
  const time_str = format.replace(/{([ymdhisa])+}/g, (result, key) => {
    const value = formatObj[key]
    // Note: getDay() returns 0 on Sunday
    if (key === 'a') {
      return ['日', '一', '二', '三', '四', '五', '六'][value]
    }
    return value.toString().padStart(2, '0')
  })
  return time_str
}

/**
 * @param {number} time
 * @param {string} option
 * @returns {string}
 */
export function formatTime(time, option) {
  if (!time) return '--'
  if (('' + time).length === 10) {
    time = parseInt(time) * 1000
  } else {
    time = +time
  }
  const d = new Date(time)
  const now = Date.now()
  const diff = (now - d) / 1000
  if (diff < 30) {
    return '刚刚'
  } else if (diff < 3600) {
    // less 1 hour
    return Math.ceil(diff / 60) + '分钟'
  } else if (diff < 3600 * 24) {
    return Math.ceil(diff / 3600) + '小时'
  } else if (diff < 3600 * 24 * 2) {
    return '1天'
  }
  if (option) {
    return parseTime(time, option)
  } else {
    return (
        d.getMonth() +
        1 +
        '月' +
        d.getDate() +
        '天' +
        d.getHours() +
        '时' +
        d.getMinutes() +
        '分'
    )
  }
}

/**
 * @param {string} url
 * @returns {Object}
 */
export function param2Obj(url) {
  const search = decodeURIComponent(url.split('?')[1]).replace(/\+/g, ' ')
  if (!search) {
    return {}
  }
  const obj = {}
  const searchArr = search.split('&')
  searchArr.forEach((v) => {
    const index = v.indexOf('=')
    if (index !== -1) {
      const name = v.substring(0, index)
      const val = v.substring(index + 1, v.length)
      obj[name] = val
    }
  })
  return obj
}

/**
 * 处理对象中为空的项
 * @param {Object} obj
 */
export function dealObjEmpty(obj) {
  const newObj = {}
  for (const key in obj) {
    if (obj[key] + '') {
      newObj[key] = obj[key]
    }
  }
  return newObj
}

/**
 * 将list装换成tree
 * @param {Object} myId 数据主键id
 * @param {Object} pId  数据关联的父级id
 * @param {Object} list list集合
 */
export function listToTree(myId, pId, list) {
  function exists(list, parentId) {
    for (const item of list) {
      if (item[myId] === parentId) return true
    }
    return false
  }

  const nodes = []
  for (const item of list) {
    if (!exists(list, item[pId])) {
      nodes.push(item)
    }
  }
  const toDo = [...nodes]
  while (toDo.length) {
    const node = toDo.shift()
    for (const item of list) {
      if (item[pId] === node[myId]) {
        if (node.children) {
          node.children.push(item)
        } else {
          node.children = [item]
        }
        toDo.push(item)
      }
    }
  }
  return nodes
}

/**
 * 树列表查-> 返回nodeId的关系id的list
 * @param list
 * @param nodeId
 * @returns {[*]}
 */
export function checkPidList(list, nodeId) {
  const nodeIdList = [nodeId]
  let status = true
  let id = nodeId
  while (status) {
    const len = list.length
    let index = 1
    for (const item of list) {
      if (item.nodeId === id) {
        nodeIdList.unshift(item.nodePid)
        id = item.nodePid
      } else {
        index++
        if (index > len) {
          status = false
        }
      }
    }
  }
  return nodeIdList
}

/**
 * @description 将tree打平成一个数组
 * @param {Object} tree
 * @returns Array
 */
export function treeToArray(tree) {
  let res = []
  for (const item of tree) {
    const { children, ...i } = item
    if (children && children.length) {
      res = res.concat(treeToArray(children))
    }
    res.push(i)
  }
  return res
}

/**
 * 给树结构添加层级信息
 */
export function addLevelAttr(tree, level = 0) {
  level += 1
  for (let index = 0; index < tree.length; index++) {
    const element = tree[index]
    element.level = level
    if (element.children) {
      addLevelAttr(element.children, level)
    }
  }
}

/**
 * 将对象的所有值转为数组
 * @param {Object} obj 传入对象
 * @returns {Array}
 */
export function objValueToArray(obj) {
  const list = []
  for (const key in obj) {
    list.push({ ...obj[key] })
  }
  return list
}

/**
 * 防抖函数
 * @param fn @type {function} 被执行的方法
 * @param delay @type {number} 毫秒 防抖期限值
 * @returns {Function}
 */
export function debounce(fn, delay) {
  let timer = null
  return () => {
    if (timer) {
      clearTimeout(timer)
      timer = setTimeout(fn, delay)
    } else {
      timer = setTimeout(fn, delay)
    }
  }
}

/**
 * 下载文件
 * @param {*} url 下载文件的地址
 * @param {*} fileName 文件名
 */
export function exportFile(url, fileName, callback) {
  const xhr = new XMLHttpRequest()
  const formData = new FormData()
  xhr.open('get', url) // url填写后台的接口地址，如果是post，在formData append参数
  xhr.setRequestHeader('Authorization', getToken())
  xhr.responseType = 'blob'
  xhr.onload = function () {
    if (this.status === 200) {
      var blob = this.response
      if (window.navigator.msSaveOrOpenBlob) {
        navigator.msSaveBlob(blob, fileName)
      } else {
        var a = document.createElement('a')
        var url = window.URL.createObjectURL(blob)
        a.href = url
        a.download = fileName
        document.body.appendChild(a)
        a.click()
        window.URL.revokeObjectURL(url)
        callback()
      }
    }
  }
  xhr.send(formData)
}

/**
 * 导出文件
 * @param {*} url 导出文件的地址
 * @param {*} fileName 文件名
 */
export async function exportData(url, callback) {
  const xhr = new XMLHttpRequest()
  xhr.open('post', url)
  xhr.setRequestHeader('Authorization', getToken())
  xhr.setRequestHeader('Content-Type', 'application/json')
  xhr.responseType = 'blob'
  xhr.onload = function () {
    if (this.status === 200) {
      var blob = this.response
      const fileName1 = this.getResponseHeader('Content-Disposition')
          .split(',')[0]
          .split('=')[1]
      const fileName = decodeURI(fileName1)
      if (window.navigator.msSaveOrOpenBlob) {
        navigator.msSaveBlob(blob, fileName)
      } else {
        var a = document.createElement('a')
        var url = window.URL.createObjectURL(blob)
        a.href = url
        a.download = fileName
        document.body.appendChild(a)
        a.click()
        window.URL.revokeObjectURL(url)
        callback()
      }
    }
  }
  xhr.send('{}')
}

// 加密字符串
export function encryText(text) {
  const encryptParam = sm4.encrypt(text, defaultSettings.publicKey)
  return encryptParam
}

export const exportDefault = 'export default'

export const beautifierConf = {
  html: {
    indent_size: '2',
    indent_char: ' ',
    max_preserve_newlines: '-1',
    preserve_newlines: false,
    keep_array_indentation: false,
    break_chained_methods: false,
    indent_scripts: 'separate',
    brace_style: 'end-expand',
    space_before_conditional: true,
    unescape_strings: false,
    jslint_happy: false,
    end_with_newline: true,
    wrap_line_length: '110',
    indent_inner_html: true,
    comma_first: false,
    e4x: true,
    indent_empty_lines: true
  },
  js: {
    indent_size: '2',
    indent_char: ' ',
    max_preserve_newlines: '-1',
    preserve_newlines: false,
    keep_array_indentation: false,
    break_chained_methods: false,
    indent_scripts: 'normal',
    brace_style: 'end-expand',
    space_before_conditional: true,
    unescape_strings: false,
    jslint_happy: true,
    end_with_newline: true,
    wrap_line_length: '110',
    indent_inner_html: true,
    comma_first: false,
    e4x: true,
    indent_empty_lines: true
  }
}

// 首字母大写
export function titleCase(str) {
  return str.replace(/( |^)[a-z]/g, (L) => L.toUpperCase())
}

export function isNumberStr(str) {
  return /^[+-]?(0|([1-9]\d*))(\.\d+)?$/g.test(str)
}

export function makeMap(str, expectsLowerCase) {
  const map = Object.create(null)
  const list = str.split(',')
  for (let i = 0; i < list.length; i++) {
    map[list[i]] = true
  }
  return expectsLowerCase ? (val) => map[val.toLowerCase()] : (val) => map[val]
}

export function dictionary(code, arr) {
  if (code === '' || code < 0) {
    return ''
  }
  const codeList = []
  const codeIsList = code instanceof Array
  for (const key in arr) {
    const curItem = arr[key]
    if (codeIsList) {
      if (
          code.includes(curItem.factValue) ||
          code.includes(Number(curItem.factValue))
      ) {
        codeList.push(curItem.displayValue)
      }
    } else if (code === curItem.factValue) {
      codeList.push(curItem.displayValue)
      break
    }
  }
  return codeList.join('、')
}

// 深拷贝对象
export function deepClone(obj) {
  const _toString = Object.prototype.toString

  // null, undefined, non-object, function
  if (!obj || typeof obj !== 'object') {
    return obj
  }

  // DOM Node
  if (obj.nodeType && 'cloneNode' in obj) {
    return obj.cloneNode(true)
  }

  // Date
  if (_toString.call(obj) === '[object Date]') {
    return new Date(obj.getTime())
  }

  // RegExp
  if (_toString.call(obj) === '[object RegExp]') {
    const flags = []
    if (obj.global) {
      flags.push('g')
    }
    if (obj.multiline) {
      flags.push('m')
    }
    if (obj.ignoreCase) {
      flags.push('i')
    }

    return new RegExp(obj.source, flags.join(''))
  }

  const result = Array.isArray(obj)
      ? []
      : obj.constructor
          ? new obj.constructor()
          : {}

  for (const key in obj) {
    result[key] = deepClone(obj[key])
  }

  return result
}

// 获取当前时间
export function getNowFormatDate() {
  var date = new Date()
  var seperator1 = '-'
  var seperator2 = ':'
  var month = date.getMonth() + 1
  var strDate = date.getDate()
  if (month >= 1 && month <= 9) {
    month = '0' + month
  }
  if (strDate >= 0 && strDate <= 9) {
    strDate = '0' + strDate
  }
  var currentdate =
      date.getFullYear() +
      seperator1 +
      month +
      seperator1 +
      strDate +
      ' ' +
      date.getHours() +
      seperator2 +
      date.getMinutes() +
      seperator2 +
      date.getSeconds()
  return currentdate
}

// 获取当前星期几
export function getWeekDay() {
  let days = new Date().getDay()

  switch (days) {
    case 1:
      days = '星期一'
      break
    case 2:
      days = '星期二'
      break
    case 3:
      days = '星期三'
      break
    case 4:
      days = '星期四'
      break
    case 5:
      days = '星期五'
      break
    case 6:
      days = '星期六'
      break
    case 0:
      days = '星期日'
      break
  }
  return days
}

// 获取时间差天数
export function getDiffDays(start, end) {
  const s = new Date(start)
  const e = new Date(end)
  const time = e.getTime() - s.getTime()
  const days = parseInt(time / (1000 * 60 * 60 * 24))
  return days
}

// 新开一个标签页
export function addNewTab(obj) {
  let list = []
  if (window.localStorage.getItem('chooseMenuList') !== '') {
    list = JSON.parse(window.localStorage.getItem('chooseMenuList'))
  }
  if (list.length === 0) {
    list.push(obj)
  } else {
    let flag = false
    list.forEach((otem) => {
      if (otem.code === obj.code) {
        flag = true
        otem.query = obj.query
      }
    })
    if (!flag) {
      list.push(obj)
    }
  }
  window.localStorage.setItem('chooseMenuList', JSON.stringify(list))
  window.localStorage.setItem('chooseMenuCode', obj.code)
  window.localStorage.setItem('chooseMenuTitle', obj.name)
  Bus.$emit('chooseMenu')
}

// 验证密码
export function validatePwd(value) {
  if (!/^(?=.*\d)(?=.*[a-zA-Z])(?=.*[\W_]).{8,14}$/.test(value)) {
    return '密码由数字、字母和特殊字符组成，长度8-14位'
  } else {
    return ''
  }
}

export const setImgPath = (item) => {
  const token = 'eyJhbGciOiJIUzUxMiJ9' // 没有token随便拼接一段
  if (item) {
    return `/apis/dj-common-api/fileserv/v2/download/${ item }/${ token }`
  } else {
    return ''
  }
}

export const imgRules = (path, defaultPath) => {
  // 使用正则判断路径中是否包含两根斜线（Windows和Unix风格）
  const hasDoubleSlash = /\\/|/\//.test(path);
  if (hasDoubleSlash) {
    return defaultPath
  } else {
   return  setImgPath(path)
  }
}
